import React, { useState } from "react";
import { IconChevronLeft, IconChevronRight, IconChevronDown } from "@tabler/icons-react";
import { getIcon } from "./IconMappings";
import { cn } from "../../lib/utils";
import "./modern-theme.css";

const ModernSidebar = ({
  structure,
  active,
  onSelect,
  openMenus = {},
  onToggle,
  collapsed = false,
  onCollapseToggle,
}) => {
  const [hoveredItem, setHoveredItem] = useState(null);
  const [hoverPosition, setHoverPosition] = useState({ top: 0 });

  const isActive = (val) => active === val;

  const groupHasActive = (items = []) =>
    items?.some((child) =>
      typeof child === "string" ? child === active : child?.value === active
    );

  const handleItemClick = (item) => {
    if (typeof item === "string") {
      onSelect(item);
    }
  };

  const handleGroupClick = (groupKey) => {
    if (!collapsed) {
      onToggle(groupKey);
    }
  };

  const handleMouseEnter = (item, event) => {
    if (collapsed) {
      const rect = event.currentTarget.getBoundingClientRect();
      setHoverPosition({ top: rect.top });
      setHoveredItem(item);
    }
  };

  const handleMouseLeave = () => {
    setHoveredItem(null);
  };

  const renderNavItem = (item, index) => {
    if (typeof item === "string") {
      return (
        <button
          key={item}
          className={cn(
            "modern-nav-item",
            isActive(item) && "active"
          )}
          onClick={() => handleItemClick(item)}
          onMouseEnter={(e) => handleMouseEnter({ type: 'single', label: item }, e)}
          onMouseLeave={handleMouseLeave}
          title={collapsed ? item : undefined}
          data-item={item}
        >
          <div className="modern-nav-icon">
            {getIcon(item, { size: 20 })}
          </div>
          {!collapsed && <span className="modern-nav-label text-white">{item}</span>}
        </button>
      );
    }

    // Handle dropdown groups (like "My Team")
    const label = item.label || "Menu";
    const key = item.key || label.toUpperCase().replace(/\s+/g, "");
    const isOpen = openMenus?.[key];
    const hasActiveChild = groupHasActive(item.items);

    return (
      <div key={key}>
        <button
          className={cn(
            "modern-nav-item",
            hasActiveChild && "active",
            isOpen && "expanded"
          )}
          onClick={() => handleGroupClick(key)}
          onMouseEnter={(e) => handleMouseEnter({ type: 'dropdown', label, items: item.items, key }, e)}
          onMouseLeave={handleMouseLeave}
          title={collapsed ? label : undefined}
          data-item={label}
        >
          <div className="modern-nav-icon">
            {getIcon(label, { size: 20 })}
          </div>
          {!collapsed && (
            <>
              <span className="modern-nav-label text-white">{label}</span>
              <IconChevronDown size={16} className="modern-nav-chevron text-white" />
            </>
          )}
        </button>

        {/* Dropdown items */}
        {!collapsed && isOpen && item.items?.length > 0 && (
          <div className="modern-nav-dropdown">
            {item.items.map((child) => {
              const childLabel = typeof child === "string" ? child : child.label || child.value;
              const childValue = typeof child === "string" ? child : child.value || childLabel;
              
              return (
                <button
                  key={childValue}
                  className={cn(
                    "modern-nav-child",
                    isActive(childValue) && "active"
                  )}
                  onClick={() => onSelect(childValue)}
                  data-item={childLabel}
                >
                  <div className="modern-nav-icon">
                    {getIcon(childLabel, { size: 16 })}
                  </div>
                  <span className="modern-nav-label text-white">{childLabel}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <aside className={cn("modern-sidebar", collapsed && "collapsed")}>
      <div className="modern-sidebar-header">
        {!collapsed && (
          <div className="modern-sidebar-title">Menu</div>
        )}
        <button
          className="modern-collapse-btn"
          onClick={onCollapseToggle}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={collapsed ? "Expand" : "Collapse"}
        >
          {collapsed ? <IconChevronRight size={16} /> : <IconChevronLeft size={16} />}
        </button>
      </div>

      <nav className="modern-sidebar-nav">
        {structure.map(renderNavItem)}
      </nav>

      {/* Floating hover tooltip for collapsed sidebar */}
      {collapsed && hoveredItem && (
        <div
          className="modern-sidebar-hover-float"
          style={{ top: hoverPosition.top }}
          onMouseEnter={() => setHoveredItem(hoveredItem)}
          onMouseLeave={handleMouseLeave}
        >
          {hoveredItem.type === 'single' ? (
            // Single item float
            <div className="hover-float-single">
              <div className="modern-nav-icon">
                {getIcon(hoveredItem.label, { size: 20 })}
              </div>
              <span className="modern-nav-label text-white">{hoveredItem.label}</span>
            </div>
          ) : (
            // Dropdown float
            <div className="hover-float-dropdown">
              <div className="hover-float-header">
                <div className="modern-nav-icon">
                  {getIcon(hoveredItem.label, { size: 20 })}
                </div>
                <span className="modern-nav-label text-white">{hoveredItem.label}</span>
                <IconChevronDown size={16} className="modern-nav-chevron text-white expanded" />
              </div>
              <div className="hover-float-items">
                {hoveredItem.items?.map((child) => {
                  const childLabel = typeof child === "string" ? child : child.label || child.value;
                  const childValue = typeof child === "string" ? child : child.value || childLabel;
                  
                  return (
                    <button
                      key={childValue}
                      className={cn(
                        "modern-nav-child",
                        isActive(childValue) && "active"
                      )}
                      onClick={() => {
                        onSelect(childValue);
                        setHoveredItem(null);
                      }}
                      data-item={childLabel}
                    >
                      <div className="modern-nav-icon">
                        {getIcon(childLabel, { size: 16 })}
                      </div>
                      <span className="modern-nav-label text-white">{childLabel}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </aside>
  );
};

export default ModernSidebar;